import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import { Eye, EyeOff, User, CheckCircle } from "lucide-react";
import axios from "axios";
import { BASE_URL } from "../configs/WebServiceUrl";
import RegistrationStepper from "../components/RegistrationStepper";


const RegistrationForm = () => {
  const navigate = useNavigate();
  const { state } = useLocation();

  const registerType = state?.register_type
    ? state?.register_type
    : "direct_signup_page";

  const backgroundMapping = {
    time_table: "/images/time-table-bg.png",
    question_paper: "/images/question papaer bg.jpeg",
    academic_calendar: "/images/calender bg.jpeg",
    documents: "/images/documents bg.jpeg",
    event_organiser: "/images/events bg.jpeg",
    teachers_planner: "/images/planning bg.jpeg",
    quiz: "/images/quiz-bg.jpeg",
  };

  const backgroundImage = backgroundMapping[registerType] || "/images/bg_general.jpg";
  const [showPassword, setShowPassword] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    mobile: "",
    password: "",
    referralCode: "",
  });
  const [errors, setErrors] = useState({});
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  const activeStepIndex = loading ? 1 : 0;

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "mobile" && value && !/^\d*$/.test(value)) return;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Name is required";
    if (!formData.mobile.trim()) {
      newErrors.mobile = "Mobile number is required";
    } else if (formData.mobile.length !== 10) {
      newErrors.mobile = "Enter a valid 10-digit mobile number";
    }
    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }
    if (!termsAccepted) {
      newErrors.terms = "Please accept the Terms & Conditions to continue";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };


  const registerUser = async () => {
  setLoading(true);

  try {
    const payload = {
      name: formData.name.trim(),
      mobile: formData.mobile.trim(),
      password: formData.password,
      register_type: registerType,
    };

    if (formData.referralCode.trim()) {
      payload.referralCode = formData.referralCode.trim();
    }

    const res = await axios.post(
      `${BASE_URL}/general/guest-registration`,
      payload
    );

    console.log("Registration success:", res.data);

    navigate("/otp", {
      state: {
        formData: {
          contact_number: formData.mobile.trim(),
        },
        source: "signup",
        role: "guest_user",
      },
    });
  } catch (err) {
    const message =
      err.response?.data?.error ||
      err.response?.data?.message ||
      "Registration failed. Please try again.";

    alert(message);
  } finally {
    setLoading(false);
  }
};

const handleSubmit = async (e) => {
  e.preventDefault();

  if (!validate()) return;

  await registerUser();
};

  // const handleSubmit = async (e) => {
  //   e.preventDefault();
  //   if (!validate()) return;
  //   setLoading(true);
  //   try {
  //     const payload = {
  //       name: formData.name.trim(),
  //       mobile: formData.mobile.trim(),
  //       password: formData.password,
  //       register_type: registerType,
  //     };
  //     if (formData.referralCode.trim()) {
  //       payload.referralCode = formData.referralCode.trim();
  //     }
  //     const res = await axios.post(`${BASE_URL}/general/guest-registration`, payload);
  //     console.log("Registration success:", res.data);
  //     navigate("/otp", {
  //       state: {
  //         formData: {
  //           contact_number: formData.mobile.trim(),
  //         },
  //         source: "signup",
  //         role: "guest_user",
  //       },
  //     });
  //   } catch (err) {
  //     const message = err.response?.data?.error || err.response?.data?.message || "Registration failed. Please try again.";
  //     alert(message);
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const handleAcceptTerms = async () => {
  setTermsAccepted(true);
  setShowTermsModal(false);

  if (errors.terms) {
    setErrors((prev) => ({
      ...prev,
      terms: "",
    }));
  }

  const tempErrors = {};

  if (!formData.name.trim()) {
    tempErrors.name = "Name is required";
  }

  if (formData.mobile.trim().length !== 10) {
    tempErrors.mobile = "Enter valid mobile number";
  }

  if (formData.password.length < 6) {
    tempErrors.password = "Password must be at least 6 characters";
  }

  setErrors(tempErrors);

  // Auto register only if no errors
  if (Object.keys(tempErrors).length === 0) {
    await registerUser();
  }
};







  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { duration: 0.6, staggerChildren: 0.15 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: "easeOut" },
    },
  };

  const formVariants = {
    hidden: { opacity: 0, scale: 0.9, y: 40 },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] },
    },
    exit: {
      opacity: 0,
      scale: 0.95,
      y: -20,
      transition: { duration: 0.3 },
    },
  };

  const successVariants = {
    hidden: { opacity: 0, scale: 0.5 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut",
        scale: { type: "spring", damping: 15, stiffness: 200 },
      },
    },
  };


  return (
    <div>
      <style>{`
        @media (max-width: 768px) {
          .signup-stepper-desktop { display: none !important; }
          .reg-main-content {
  background: transparent !important;
  align-items: center !important;
  justify-content: center !important;
  min-height: 100vh !important;
}
          .reg-content-col {
            padding: 20px 16px 32px !important;
            max-width: 100% !important;
          }
          .reg-content-col > div,
          .reg-content-col > [class] {
            border-radius: 14px !important;
          }

          /* Field row: label | input using Grid */
          .reg-field-group {
            display: grid !important;
            grid-template-columns: 85px 1fr !important;
            align-items: center !important;
            column-gap: 8px !important;
            row-gap: 2px !important;
          }
          .reg-field-group label {
            font-size: 12px !important;
            font-weight: 600 !important;
            white-space: nowrap !important;
            margin: 0 !important;
            line-height: 1.3 !important;
          }
          .reg-input-wrapper {
            position: relative !important;
            width: 100% !important;
          }
          .reg-field-group input {
            display: block !important;
            width: 100% !important;
            box-sizing: border-box !important;
            height: 36px !important;
            font-size: 13px !important;
            padding: 0 12px !important;
            background: #e8edf5 !important;
            border: 1.5px solid #94a3b8 !important;
            border-radius: 8px !important;
          }
          /* Error text spans to second column */
          .reg-field-group > span {
            grid-column: 2 !important;
          }
        }
        @media (min-width: 769px) {
          .signup-stepper-mobile { display: none !important; }
        }
          label > span{
 position:relative;
 left:10px;
}

/* Input */
#root div input[type=checkbox]{
 position:relative;
 bottom:-3.5px;
}
      `}</style>

      <div
        style={{
          ...styles.pageWrapper,
          ...(backgroundMapping[registerType]
            ? {
              backgroundImage: `linear-gradient(rgba(0,0,0,0.45), rgba(0,0,0,0.45)), url("${backgroundImage}")`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
            }
            : {
              background: "linear-gradient(135deg, #4a7fc5 0%, #3a9ea8 50%, #38b89c 100%)",
            }),
        }}
      >
        <div style={styles.signupLayout}>
          <div className="signup-stepper-desktop" style={styles.signupSidebar}>
            <RegistrationStepper activeStep={activeStepIndex} />
          </div>
          <div className="reg-main-content" style={styles.mainContent}>
            <div className="reg-content-col" style={styles.contentCol}>

              {/* Notice card — TOP of content area, wide */}
              {/* <div className="d-none d-md-block" style={styles.noticeCard}>
                <p style={{ margin: 0, fontSize: "13px", fontWeight: 800, color: "#667eea", lineHeight: 1.6 }}>
                  आगे बढ़ने से पहले कृपया अपने आपको रजिस्टर कर लें।
                </p>
                <p style={{ margin: "4px 0 0", fontSize: "11px", fontWeight: 600, color: "#667eea", lineHeight: 1.6 }}>
                  मॉड्यूल  सेव करने और प्रगति ट्रैक करने के लिए रजिस्ट्रेशन ज़रूरी है।
                </p>
                <div style={{ width: "32px", height: "2px", background: "#667eea", borderRadius: "2px", margin: "8px auto" }} />
                <p style={{ margin: 0, fontSize: "12px", fontWeight: 500, color: "#667eea", lineHeight: 1.6, fontStyle: "italic" }}>
                  Registration is required to save work in modules & track progress..
                </p>
              </div> */}

              {/* Form card — below the notice */}
              <motion.div
                style={styles.container}
                variants={containerVariants}
                initial="hidden"
                animate="visible"
              >
                <motion.div style={styles.headerSection} variants={itemVariants}>
                  <h2 style={styles.title}>Register to Proceed</h2>
                  <div className="signup-stepper-mobile" style={{ marginTop: "12px" }}>
                    <RegistrationStepper activeStep={activeStepIndex} />
                  </div>
                </motion.div>

                <AnimatePresence mode="wait">
                  {!submitted && (
                    <motion.form
                      key="form"
                      onSubmit={handleSubmit}
                      variants={formVariants}
                      initial="hidden"
                      animate="visible"
                      exit="exit"
                      style={styles.form}
                    >
                      {/* Name Field */}
                      <motion.div
                        className="reg-field-group"
                        style={styles.fieldGroup}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.1, duration: 0.4 }}
                      >
                        <label style={styles.label}>{isMobile ? "Name" : "Full Name"}</label>
                        <div className="reg-input-wrapper" style={styles.inputWrapper}>
                          <input
                            type="text"
                            name="name"
                            placeholder="Enter your full name"
                            value={formData.name}
                            onChange={handleChange}
                            style={{
                              ...styles.input,
                              ...(errors.name ? styles.inputError : {}),
                            }}
                          />
                        </div>
                        {errors.name && (
                          <motion.span
                            style={styles.errorText}
                            initial={{ opacity: 0, y: -5 }}
                            animate={{ opacity: 1, y: 0 }}
                          >
                            {errors.name}
                          </motion.span>
                        )}
                      </motion.div>

                      {/* Mobile Field */}
                      <motion.div
                        className="reg-field-group"
                        style={styles.fieldGroup}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.2, duration: 0.4 }}
                      >
                        <label style={styles.label}>{isMobile ? "Mobile" : "Mobile Number"}</label>
                        <div className="reg-input-wrapper" style={styles.inputWrapper}>
                          <input
                            type="tel"
                            name="mobile"
                            placeholder="Enter 10-digit mobile number"
                            value={formData.mobile}
                            onChange={handleChange}
                            maxLength={10}
                            style={{
                              ...styles.input,
                              ...(errors.mobile ? styles.inputError : {}),
                            }}
                          />
                        </div>
                        {errors.mobile && (
                          <motion.span
                            style={styles.errorText}
                            initial={{ opacity: 0, y: -5 }}
                            animate={{ opacity: 1, y: 0 }}
                          >
                            {errors.mobile}
                          </motion.span>
                        )}
                      </motion.div>

                      {/* Password Field */}
                      <motion.div
                        className="reg-field-group"
                        style={styles.fieldGroup}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.3, duration: 0.4 }}
                      >
                        <label style={styles.label}>Password</label>
                        <div className="reg-input-wrapper" style={styles.inputWrapper}>
                          <input
                            type={showPassword ? "text" : "password"}
                            name="password"
                            placeholder="Create a password"
                            value={formData.password}
                            onChange={handleChange}
                            style={{
                              ...styles.input,
                              ...(errors.password ? styles.inputError : {}),
                            }}
                          />
                          <span
                            style={styles.eyeIcon}
                            onClick={() => setShowPassword(!showPassword)}
                          >
                            {showPassword ? (
                              <EyeOff size={18} color="#94a3b8" />
                            ) : (
                              <Eye size={18} color="#94a3b8" />
                            )}
                          </span>
                        </div>
                        {errors.password && (
                          <motion.span
                            style={styles.errorText}
                            initial={{ opacity: 0, y: -5 }}
                            animate={{ opacity: 1, y: 0 }}
                          >
                            {errors.password}
                          </motion.span>
                        )}
                      </motion.div>

                      {/* Referral Code Field (Optional) */}
                      <motion.div
                        className="reg-field-group"
                        style={styles.fieldGroup}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.35, duration: 0.4 }}
                      >
                        <label style={styles.label}>
                          {isMobile ? "Referral Code" : <>Referral Code <span style={styles.optionalTag}>(Optional)</span></>}
                        </label>
                        <div className="reg-input-wrapper" style={styles.inputWrapper}>
                          <input
                            type="text"
                            name="referralCode"
                            placeholder="Enter referral code if you have one"
                            value={formData.referralCode}
                            onChange={handleChange}
                            style={styles.input}
                          />
                        </div>
                      </motion.div>

                      {/* Terms & Conditions Checkbox */}
                      <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.38, duration: 0.4 }}
                        style={{ display: "flex", flexDirection: "column", gap: "4px" }}
                      >
                        <label style={{ display: "flex", alignItems: "center", gap: "10px", cursor: "pointer", userSelect: "none" }}>
                          <input
                            type="checkbox"
                            checked={termsAccepted}
                            onChange={() => { }}
                            onClick={(e) => { e.preventDefault(); setShowTermsModal(true); }}
                            style={{ width: "16px", height: "16px", accentColor: "#3b82f6", flexShrink: 0, cursor: "pointer", margin: 0 }}
                          />
                          <span style={{ fontSize: "13px", color: "#475569" }}>
                            I agree to the{" "}
                            <span
                              onClick={(e) => { e.preventDefault(); setShowTermsModal(true); }}
                              style={{ color: "#3b82f6", fontWeight: "600", textDecoration: "underline", cursor: "pointer" }}
                            >
                              Terms &amp; Conditions
                            </span>
                          </span>
                        </label>
                        {errors.terms && (
                          <motion.span style={styles.errorText} initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }}>
                            {errors.terms}
                          </motion.span>
                        )}
                      </motion.div>

                      {/* Submit Button */}
                      <motion.button
                        type="submit"
                        disabled={loading}
                        style={{
                          ...styles.submitButton,
                          ...(loading ? { opacity: 0.7, cursor: "not-allowed" } : {}),
                        }}
                        whileHover={loading ? {} : { scale: 1.02, boxShadow: "0 8px 30px rgba(0, 101, 255, 0.4)" }}
                        whileTap={loading ? {} : { scale: 0.97 }}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.45, duration: 0.4 }}
                      >
                        {loading ? "Registering..." : "Create Account"}
                      </motion.button>

                      <motion.p
                        style={styles.formFooterText}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.5 }}
                      >
                        Already have an account?{" "}
                        <span style={styles.link} onClick={() => navigate("/login")}>
                          Log in
                        </span>
                      </motion.p>
                    </motion.form>
                  )}

                  {submitted && (
                    <motion.div
                      key="success"
                      variants={successVariants}
                      initial="hidden"
                      animate="visible"
                      style={styles.successSection}
                    >
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{
                          type: "spring",
                          damping: 10,
                          stiffness: 200,
                          delay: 0.2,
                        }}
                      >
                        <CheckCircle size={64} color="#22c55e" />
                      </motion.div>
                      <h3 style={styles.successTitle}>Registration Successful!</h3>
                      <p style={styles.successText}>
                        Your account has been created. You can now sign in.
                      </p>
                      <motion.button
                        style={styles.successButton}
                        onClick={() => navigate("/login")}
                        whileHover={{ scale: 1.03 }}
                        whileTap={{ scale: 0.97 }}
                      >
                        Go to Login
                      </motion.button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>

            </div>{/* end contentCol */}
          </div>
        </div>
      </div>

      {/* Terms & Conditions Modal */}
      {showTermsModal && (
        <div
          onClick={() => setShowTermsModal(false)}
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)",
            zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center",
            padding: "20px",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#fff", borderRadius: "16px", width: "100%", maxWidth: "560px",
              maxHeight: "80vh", display: "flex", flexDirection: "column",
              boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
            }}
          >
            {/* Modal Header */}
            <div style={{
              padding: "20px 24px 16px", borderBottom: "1px solid #e2e8f0",
              display: "flex", alignItems: "center", justifyContent: "space-between",
            }}>
              <h2 style={{ margin: 0, fontSize: "18px", fontWeight: "700", color: "#1e293b" }}>
                Terms &amp; Conditions
              </h2>
              <span
                onClick={() => setShowTermsModal(false)}
                style={{ fontSize: "22px", cursor: "pointer", color: "#94a3b8", lineHeight: 1 }}
              >
                &times;
              </span>
            </div>
            {/* Modal Body */}
            <div style={{ padding: "20px 24px", overflowY: "auto", flex: 1, fontSize: "13px", color: "#475569", lineHeight: "1.7" }}>
              <p><strong>1. Acceptance of Terms</strong><br />By registering on DocMaster, you agree to be bound by these Terms &amp; Conditions. If you do not agree, please do not use our services.</p>
              <p><strong>2. Use of Services</strong><br />DocMaster provides tools for creating question papers, timetables, academic calendars, and other school management utilities. You agree to use these services only for lawful educational purposes.</p>
              <p><strong>3. Account Responsibility</strong><br />You are responsible for maintaining the confidentiality of your account credentials. All activities under your account are your responsibility.</p>
              <p><strong>4. Data Privacy</strong><br />Your personal information is collected and used in accordance with our Privacy Policy. We do not sell or share your data with third parties without your consent.</p>
              <p><strong>5. Subscription &amp; Payments</strong><br />Paid features are available at ₹250/- per year. All payments are non-refundable unless stated otherwise in our Refund &amp; Cancellation Policy.</p>
              <p><strong>6. Intellectual Property</strong><br />All content, logos, and software on DocMaster are the intellectual property of Shri Subhash Parasram Electronic Private Limited. Unauthorized reproduction is prohibited.</p>
              <p><strong>7. Modifications</strong><br />We reserve the right to update these terms at any time. Continued use of our services after changes constitutes acceptance of the updated terms.</p>
              <p><strong>8. Contact</strong><br />For queries, contact us at: The Electronic City, A 75 Sector 63 Noida | 9289 44 00 49</p>
            </div>
            {/* Modal Footer */}
            <div style={{ padding: "16px 24px", borderTop: "1px solid #e2e8f0", display: "flex", gap: "12px", justifyContent: "flex-end" }}>
              <button
                onClick={() => setShowTermsModal(false)}
                style={{ padding: "10px 20px", borderRadius: "8px", border: "1px solid #cbd5e1", background: "#f8fafc", color: "#475569", fontSize: "14px", cursor: "pointer" }}
              >
                Close
              </button>
              <button
                onClick={handleAcceptTerms}
                style={{ padding: "10px 20px", borderRadius: "8px", border: "none", background: "#3b82f6", color: "#fff", fontSize: "14px", fontWeight: "600", cursor: "pointer" }}
              >
                I Accept
              </button>
            </div>
          </div>
        </div>
      )}

      <style>
    {`
      input[type="password"]::-ms-reveal,
      input[type="password"]::-ms-clear {
        display: none;
      }

      input::-webkit-credentials-auto-fill-button,
      input::-webkit-password-toggle-button {
        display: none !important;
        visibility: hidden;
        pointer-events: none;
      }
    `}
  </style>
    </div>
  );
};

const styles = {
  pageWrapper: {
    minHeight: "100vh",
    display: "flex",
    background: "linear-gradient(135deg, #4a7fc5 0%, #3a9ea8 50%, #38b89c 100%)",
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  },
  contentCol: {
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    width: "100%",
    maxWidth: "540px",
    padding: "28px 32px 40px 32px",
    gap: "20px",
    position: "relative",
    zIndex: 2,
  },
  noticeCard: {
    width: "100%",
    padding: "16px 20px",
    textAlign: "center",
    borderRadius: "14px",
    background: "rgba(255, 255, 255, 0.95)",
    border: "1px solid rgba(102, 126, 234, 0.2)",
    boxShadow: "0 4px 16px rgba(15, 23, 42, 0.08)",
  },
  signupLayout: {
    width: "100%",
    minHeight: "100vh",
    display: "flex",
    alignItems: "stretch",
    justifyContent: "start",
    gap: "0",
    position: "relative",
    zIndex: 1,
    overflow: "hidden",
  },
  signupSidebar: {
    width: "250px",
    minWidth: "250px",
    flexShrink: "0",
    display: "flex",
  },
  mainContent: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  container: {
    width: "100%",
    background: "rgba(255, 255, 255, 0.97)",
    backdropFilter: "blur(10px)",
    borderRadius: "20px",
    padding: "20px 28px 24px",
    boxShadow: "0 4px 6px rgba(0,0,0,0.04), 0 20px 50px rgba(0,0,0,0.12)",
    textAlign: "center",
    overflow: "hidden",
  },
  headerSection: {
    marginBottom: "12px",
  },
  logoCircle: {
    width: "64px",
    height: "64px",
    borderRadius: "50%",
    background: "linear-gradient(135deg, #0065ff, #5a4fcf)",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: "20px",
  },
  title: {
    fontSize: "24px",
    fontWeight: "700",
    color: "#1e293b",
    margin: "0 0 8px 0",
  },
  subtitle: {
    fontSize: "15px",
    color: "#64748b",
    margin: "0",
  },
  link: {
    color: "#0065ff",
    fontWeight: "600",
    cursor: "pointer",
    textDecoration: "none",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    textAlign: "left",
  },
  fieldGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
  },
  label: {
    fontSize: "14px",
    fontWeight: "600",
    color: "#334155",
    margin: 0,
    lineHeight: 1.2,
  },
  optionalTag: {
    fontSize: "12px",
    fontWeight: "400",
    color: "#94a3b8",
  },
  inputWrapper: {
    position: "relative",
    display: "flex",
    alignItems: "center",
  },
  input: {
    width: "100%",
    height: "38px",
    padding: "0 14px",
    fontSize: "15px",
    lineHeight: "38px",
    color: "#1e293b",
    background: "#f8fafc",
    border: "2px solid #e2e8f0",
    borderRadius: "10px",
    outline: "none",
    transition: "border-color 0.2s ease, box-shadow 0.2s ease",
    boxSizing: "border-box",
  },
  inputError: {
    borderColor: "#ef4444",
  },
  eyeIcon: {
    position: "absolute",
    right: "14px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
  },
  errorText: {
    fontSize: "13px",
    color: "#ef4444",
    marginTop: "2px",
  },
  submitButton: {
    width: "100%",
    padding: "12px 24px",
    fontSize: "16px",
    fontWeight: "600",
    color: "#ffffff",
    background: "linear-gradient(135deg, #0065ff, #5a4fcf)",
    border: "none",
    borderRadius: "12px",
    cursor: "pointer",
    marginTop: "4px",
  },
  formFooterText: {
    fontSize: "14px",
    color: "#64748b",
    textAlign: "center",
    margin: "0",
  },
  successSection: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "16px",
    padding: "20px 0",
  },
  successTitle: {
    fontSize: "22px",
    fontWeight: "700",
    color: "#1e293b",
    margin: "0",
  },
  successText: {
    fontSize: "15px",
    color: "#64748b",
    margin: "0",
  },
  successButton: {
    padding: "12px 32px",
    fontSize: "15px",
    fontWeight: "600",
    color: "#ffffff",
    background: "linear-gradient(135deg, #22c55e, #16a34a)",
    border: "none",
    borderRadius: "12px",
    cursor: "pointer",
    marginTop: "8px",
  },
};

export default RegistrationForm;